import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CaretRight, ShieldCheck } from '@phosphor-icons/react';
import { navigate } from '../lib/router';
import { ASSETS, NETWORKS, type Fiat, type Lang, type MeDTO } from '@onyx/shared';
import { useConfig, useMe } from '../lib/queries';
import { useLang, useT } from '../lib/i18n';
import { formatCrypto } from '../lib/format';
import { standardTag } from '../lib/networks';
import { api } from '../lib/api';
import { setPref, usePrefs, type ThemePref } from '../lib/prefs';
import { copyText } from '../lib/telegram';
import { AssetChip } from '../components/AssetChip';
import { Avatar, Field, Notice, Screen, ScreenHeader, Section, Segmented, toast } from '../components/ui';

export function Settings() {
  const t = useT();
  const lang = useLang();
  const me = useMe();
  const config = useConfig();
  const prefs = usePrefs();
  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: (v: Partial<{ fiat: Fiat; lang: Lang }>) => api<{ me: MeDTO }>('/me', { method: 'PATCH', body: v }),
    onSuccess: (r) => qc.setQueryData(['me'], (old: { me: MeDTO; startParam: string | null } | undefined) => ({ startParam: old?.startParam ?? null, me: r.me })),
  });
  const user = me.data?.me;

  return (
    <Screen className="settings">
      <ScreenHeader title={t['settings.title']} />
      {user && (
        <div className="profile">
          <Avatar user={user} size={56} />
          <div>
            <p className="profile__name">
              {user.firstName} {user.lastName ?? ''}
            </p>
            <button type="button" className="profile__id num" onClick={() => void copyText(user.tgId).then(() => toast.good(t['deposit.copied']))}>
              {user.username ? `@${user.username} · ` : ''}
              {t['settings.id']} {user.tgId}
            </button>
          </div>
        </div>
      )}

      {user?.isAdmin && (
        <button type="button" className="card soft-row" onClick={() => navigate('/admin')}>
          <span className="soft-row__icon">
            <ShieldCheck size={22} weight="fill" aria-hidden="true" />
          </span>
          <span className="soft-row__text">
            <span className="soft-row__title">{t['settings.admin']}</span>
            <span className="soft-row__sub">{t['settings.adminSub']}</span>
          </span>
          <CaretRight size={20} weight="bold" className="soft-row__chev" aria-hidden="true" />
        </button>
      )}

      <Field label={t['settings.currency']}>
        <Segmented<Fiat>
          label={t['settings.currency']}
          value={user?.fiat ?? 'USD'}
          onChange={(fiat) => update.mutate({ fiat })}
          options={[
            { value: 'USD', label: 'USD $' },
            { value: 'RUB', label: 'RUB ₽' },
            { value: 'EUR', label: 'EUR €' },
          ]}
        />
      </Field>
      <Field label={t['settings.language']}>
        <Segmented<Lang>
          label={t['settings.language']}
          value={user?.lang ?? 'ru'}
          onChange={(l) => update.mutate({ lang: l })}
          options={[
            { value: 'ru', label: 'Русский' },
            { value: 'en', label: 'English' },
          ]}
        />
      </Field>
      <Field label={t['settings.theme']}>
        <Segmented<ThemePref>
          label={t['settings.theme']}
          value={prefs.theme}
          onChange={(v) => setPref('theme', v)}
          options={[
            { value: 'auto', label: t['settings.theme.auto'] },
            { value: 'dark', label: t['settings.theme.dark'] },
            { value: 'light', label: t['settings.theme.light'] },
          ]}
        />
      </Field>

      <Section title={t['settings.fees']}>
        <Notice>
          {t['settings.feesNote']}
          {config.data && <> {t['settings.swapFee'](lang === 'ru' ? config.data.swapFeePercent.replace('.', ',') : config.data.swapFeePercent)}.</>}
        </Notice>
        <div className="fees" role="table" aria-label={t['settings.fees']}>
          <div className="fees__head" role="row">
            <span role="columnheader">{t['withdraw.network']}</span>
            <span role="columnheader">{t['pick.min']}</span>
            <span role="columnheader">{t['pick.fee']}</span>
          </div>
          {ASSETS.map((a) => (
            <div key={a.code} className="fees__group" role="rowgroup">
              <div className="fees__asset">
                <AssetChip asset={a.code} size={24} />
                <span>{a.code}</span>
              </div>
              {a.networks.map((n) => (
                <div key={n.network} className="fees__row" role="row">
                  <span role="cell">
                    {NETWORKS[n.network].name}
                    {standardTag(n.standard) && <span className="tag">{n.standard}</span>}
                  </span>
                  <span role="cell" className="num">{formatCrypto(n.minWithdraw, 8, lang)}</span>
                  <span role="cell" className="num">{formatCrypto(n.withdrawFee, 8, lang)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>
    </Screen>
  );
}
